import type { RouteConfig } from 'vue-router';

import Vue from 'vue';

import { cabinetRoutes as legacyCabinetRoutes } from './legacy-routes';

import { useProfileStore } from '@/store/profile';
import { useSidepanelStore } from '@/store/side-panel';

import SiteFooter from '@/components/common/SiteFooter.vue';

const CabinetLayout = defineAsyncComponent(() => import('@/layouts/CabinetLayout.vue'));
const SimpleLayout = defineAsyncComponent(() => import('@/layouts/SimpleLayout.vue'));
const SimpleAppBar = defineAsyncComponent(() => import('@/components/widgets/app-bar/SimpleAppBar.vue'));

const RoleBasedCabinetSidebar = defineAsyncComponent(() => import('@/components/cabinet/RoleBasedCabinetSidebar.vue'));
const SimpleGridView = defineAsyncComponent(() => import('@/views/SimpleGridView.vue'));

const routes: RouteConfig[] = [
  {
    name: 'index',
    path: '/',
    meta: {
      pageTitle: null,
    },
    // перенаправляем на кабинет, там есть проверка на авторизацию, если не авторизован перенапрвит на account/login
    redirect: { name: 'Cabinet' },
  },
  {
    name: 'account',
    path: '/account',
    components: {
      default: SimpleLayout,
      bar: SimpleAppBar,
      footer: SiteFooter,
    },
    meta: {
      pageTitle: null,
    },
    children: [
      {
        name: 'register',
        meta: {
          pageTitle: 'Регистрация',
        },
        path: 'register',
        component: defineAsyncComponent(() => import('@/views/RegisterView.vue')),
      },
      {
        name: 'login',
        path: 'login',
        alias: '',
        meta: {
          pageTitle: 'Авторизация',
        },
        component: defineAsyncComponent(() => import('@/views/LoginView.vue')),
      },
      {
        name: 'recovery',
        path: 'recovery',
        meta: {
          pageTitle: 'Восстановления пароля',
        },
        component: defineAsyncComponent(() => import('@/views/RecoveryView.vue')),
      },
    ],
  },
  {
    path: '/cabinet',
    name: 'Cabinet',
    meta: {
      pageTitle: null,
      breadcrumbTitle: 'Главная',
    },
    components: {
      default: CabinetLayout,
      bar: defineAsyncComponent(() => import('@/components/widgets/app-bar/MainAppBar.vue')),
      footer: SiteFooter,
    },
    async beforeEnter(to, from, next) {
      const profileStore = useProfileStore();
      await profileStore.fetchServerData().catch(console.error);

      if (to.meta?.guestRole) {
        return next();
      }

      if (!profileStore.isAuthenticated) {
        // разрешаем переход на просмотр страниц для которых разрешен режим гостя
        return next({ name: 'login', query: { redirectTo: to.path } });
      }

      let finalCompanyId: string | undefined;
      if (!profileStore.roleSide && to.name !== 'pickMemberRole') {
        finalCompanyId = profileStore.findAnyCompanyByRole('investor')?.id || profileStore.findAnyCompanyByRole('borrower')?.id; // сначала ищем инвестора, потом заемщика
        if (!finalCompanyId) {
          return next({ name: 'pickMemberRole' });
        }
      }
      else if (profileStore.roleSide && !profileStore.companyId) {
        finalCompanyId = profileStore.findAnyCompanyByRole(profileStore.roleSide)?.id;
      }

      if (finalCompanyId) {
        await profileStore.switchToCompany({ companyId: finalCompanyId });
      }

      next();
    },
    children: [
      ...legacyCabinetRoutes,
      {
        name: 'pickMemberRole',
        path: 'member-role',
        meta: {
          pageTitle: 'Выбор роли участника',
        },
        component: defineAsyncComponent(() => import('@/views/MemberRoleView.vue')),
      },
      {
        name: 'board',
        path: 'board',
        components: {
          default: defineAsyncComponent(() => import('@/components/cabinet/RoleBasedCabinetBoard.vue')),
          sidebar: RoleBasedCabinetSidebar,
        },
      },
      {
        path: 'transactions',
        component: SimpleGridView,
        props: {
          default: {
            backButtonRoute: {
              name: 'board',
            },
          },
        },
        children: [
          {
            name: 'transactionsList',
            path: '/',
            alias: 'list',
            meta: {
              pageTitle: 'Список операций',
            },
            component: defineAsyncComponent(() => import('@/pages/TransactionGrid.vue')),
          },
        ],
      },
      {
        path: 'projects-market',
        component: defineAsyncComponent(() => import('@/pages/ProjectsMarket.vue')),
        meta: {
          role: 'investor',
          pageTitle: 'Рынок',
        },
      },
      {
        path: 'invest-portfolio',
        component: defineAsyncComponent(() => import('@/pages/InvestPortfolio.vue')),
        meta: {
          role: 'investor',
          pageTitle: 'Портфель',
        },
      },
      {
        name: 'project-direct',
        path: 'projects/:num([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})/:human?', // project direct url,
        meta: {
          pageTitle: 'Просмотр проекта', // TODO: сюда бы вывести номер проекта
          guestRole: 'investor',
        },
        components: {
          default: defineAsyncComponent(() => import('@/views/ProjectView.vue')),
          sidebar: defineAsyncComponent(() => import('@/components/project/sidebar/RoleBasedProjectSidebar.vue')),
        },
      },
      {
        name: 'investorRegistration',
        path: 'investor-registration',
        meta: {
          pageTitle: 'Регистрация инвестора',
          role: 'investor',
          hideAppBarNavigation: true,
        },
        component: defineAsyncComponent(() => import('@/pages/InvestorRegistration.vue')),
      },
      {
        name: 'investorGetQualified',
        path: 'qualified-investor',
        meta: {
          pageTitle: 'Заявка на квалиф. инвестора',
          role: 'investor',
          hideAppBarNavigation: true,
        },
        component: defineAsyncComponent(() => import('@/views/investor/InvestorQualifiedApp.vue')),
      },
      {
        name: 'loanApp',
        path: 'loan-app/:num([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})?', // direct url to loan app (new or fill)
        alias: 'loan-app/new',
        meta: {
          pageTitle: 'Заполнение заявки', // TODO: сюда бы вывести статус движения заявки?
          role: 'borrower',
          hideAppBarNavigation: true,
        },
        props: (route) => {
          if (route.path.endsWith('/new') || (!route.params.num && route.params.isBare)) {
            return {
              isBare: true,
            };
          }
        },
        component: defineAsyncComponent(() => import('@/views/borrower/BorrowerAppView.vue')),
      },
      {
        path: 'autoinvest-app/new',
        meta: {
          pageTitle: 'Новая заявка на автоинвестирование/следование',
          breadcrumbTitle: 'Новая заявка',
          role: 'investor',
        },
        props: (route) => {
          return {
            useInternalView: route.meta?.useInternalView,
          };
        },
        component: defineAsyncComponent(() => import('@/views/investor/AutoinvestAppNew.vue')),
        children: [
          {
            path: 'auto-app',
            meta: {
              useInternalView: true,
              pageTitle: 'Новая заявка на автоинвестирование',
              breadcrumbTitle: 'Автоинвестирование',
            },
            component: defineAsyncComponent(() => import('@/views/investor/AutoinvestApp/AutoApp.vue')),
          },

          {
            path: 'auto-follow',
            meta: {
              useInternalView: true,
              pageTitle: 'Новая заявка на автоследование',
              breadcrumbTitle: 'Автоследование',
            },
            component: defineAsyncComponent(() => import('@/views/investor/AutoinvestApp/AutoFollow.vue')),
          },
        ],
      },
      {
        path: 'fill',
        meta: {
          pageTitle: 'Регистрация заемщика',
          role: 'borrower',
          hideAppBarNavigation: true,
        },
        beforeEnter(to, from, next) {
          const profileStore = useProfileStore();

          if (profileStore.borrowerAppRegState === 'done') {
            return next({ name: 'board' });
          }

          return next();
        },
        component: defineAsyncComponent(() => import('@/views/borrower/BorrowerFillView.vue')),
      },
      {
        name: 'profile',
        path: 'profile',
        component: defineAsyncComponent(() => import('@/views/ProfileView.vue')),
      },
      {
        path: 'ticket/:ticketNum',
        alias: 't/:ticketNum',
        beforeEnter(to, from, next) {
          if (to.params.ticketNum) {
            const sidePanelStore = useSidepanelStore();
            sidePanelStore.spawn({
              name: 'Tickets',
              props: {
                ticketNum: to.params.ticketNum,
              },
            });
          }

          return next({ name: 'board' });
        },
      },
    ],
  },
  {
    // таск отправляет свой ид, перенаправляем на страницу проекта инвестора
    path: '/resolve-project/:id',
    async beforeEnter(to, from, next) {
      const { response } = await Vue.http.post('/admin/ext/task/resolve_project_by_id', {
        id: to.params.id,
      });

      const project_id = response?.project_id;
      if (!project_id) {
        Vue.notify({
          type: 'error',
          text: 'Проект не найден',
          duration: -1,
        });
        return;
      }

      return next({ name: 'project-direct', params: { num: response.project_id } });
    },
  },
  {
    path: '*',
    redirect: '/account/login',
  },
];

export { routes as default, routes };
